import React, { useState, useRef, useEffect } from "react";
import { updateOnboardingStep } from "./dataProvider";
import useTour from "../hooks/useTour";
import { getTourConfig } from "../config/tourConfigs";
import {
  TabbedForm,
  FormTab,
  SelectInput,
  Edit,
  required,
  ArrayInput,
  SimpleFormIterator,
  useEditController,
  Loading,
  useDataProvider,
  useNotify,
} from "react-admin";
import { FormDataConsumer } from "react-admin";
import { useContext } from "react";
import { OnboardingContext } from "../contexts/OnboardingContext";
const PREFIX = "AvailabilityEdit";

const classes = {
  inlineBlock: `${PREFIX}-inlineBlock`,
};

export const hours = [
  { id: 0, name: "00" },
  { id: 1, name: "01" },
  { id: 2, name: "02" },
  { id: 3, name: "03" },
  { id: 4, name: "04" },
  { id: 5, name: "05" },
  { id: 6, name: "06" },
  { id: 7, name: "07" },
  { id: 8, name: "08" },
  { id: 9, name: "09" },
  { id: 10, name: "10" },
  { id: 11, name: "11" },
  { id: 12, name: "12" },
  { id: 13, name: "13" },
  { id: 14, name: "14" },
  { id: 15, name: "15" },
  { id: 16, name: "16" },
  { id: 17, name: "17" },
  { id: 18, name: "18" },
  { id: 19, name: "19" },
  { id: 20, name: "20" },
  { id: 21, name: "21" },
  { id: 22, name: "22" },
  { id: 23, name: "23" },
];

export const getFinalFor = (initial) => {
  if (initial) return hours.filter((e) => e.id > initial);
  return hours;
};

export const getInitialFor = (final) => {
  if (final) return hours.filter((e) => e.id < final);
  return hours;
};

export const AvailabilityEdit = (props) => {
  const controllerProps = useEditController(props); // Hook de react-admin
  const { isLoading } = controllerProps;
  // const { isActive, currentPage, currentStep } = useTour();
  // Acceso directo a la instancia de driver.js
  const driverInstanceRef = useRef(null);
  const { setOnboardingSteps, onboardingSteps, checkAndTriggerOnboarding } = useContext(OnboardingContext);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  // Estado para resaltar el bloque expandido
  const [highlight, setHighlight] = useState(false);
  const highlightTimeout = useRef();

  // Handler para el botón +
  const handleAddHorarioClick = () => {
    // Inicia el tour directamente al hacer click, sin condicionales
    const driverGlobal = window.driver || (window.__driverjs__ && window.__driverjs__.instance);
    if (driverGlobal && typeof driverGlobal.next === "function") {
      driverGlobal.next();
    } else if (driverInstanceRef.current && typeof driverInstanceRef.current.next === "function") {
      driverInstanceRef.current.next();
    }
    setHighlight(true);
    highlightTimeout.current = setTimeout(() => setHighlight(false), 1200);
  };

  // Función para convertir día a dayIndex
  const getDayIndex = (day) => {
    const dayMap = {
      L: 0, // Lunes
      M: 1, // Martes
      MM: 2, // Miércoles
      J: 3, // Jueves
      V: 4, // Viernes
      S: 5, // Sábado
      D: 6, // Domingo
    };
    return dayMap[day] || 0;
  };

  // Handler para guardar disponibilidad y luego actualizar onboarding
  const handleSave = async (values, redirect) => {
    try {
      // Calcular dayIndex para cada elemento de availability antes de enviar
      if (values.availability && Array.isArray(values.availability)) {
        values.availability = values.availability.map((item) => ({
          ...item,
          dayIndex: getDayIndex(item.day),
        }));
      }

      // Primero guarda la disponibilidad
      await dataProvider.update("availabilityWeb", { id: values._id, data: values });
      await updateOnboardingStep(
        { userId: values._id, step: 4 },
        { setOnboardingSteps, checkAndTriggerOnboarding, onboardingSteps }
      );

      // Mostrar notificación de éxito
      notify("Disponibilidad guardada correctamente", { type: "success" });
    } catch (err) {
      // El error puede venir como string directo desde el dataProvider o como objeto
      let errorMessage = "";

      if (typeof err === "string") {
        errorMessage = err;
      } else {
        errorMessage = err?.message || err?.body?.error || err?.error || "";
      }

      if (err?.status && err.status >= 400) {
        // Errores del servidor con status HTTP
        const serverErrorMessage =
          err?.body?.error || err?.message || err?.error || "Error actualizando la disponibilidad";
        notify(`Error: ${serverErrorMessage}`, { type: "error" });
      } else if (errorMessage) {
        // Error con mensaje específico
        notify(`Error: ${errorMessage}`, { type: "error" });
      } else {
        // Error genérico
        notify("Error actualizando la disponibilidad. Intenta de nuevo.", { type: "error" });
      }
    }
    // Continúa con el guardado normal
    if (props.onSave) {
      props.onSave(values, redirect);
    }
  };

  // Limpiar timeout si el componente se desmonta
  useEffect(() => {
    return () => {
      if (highlightTimeout.current) clearTimeout(highlightTimeout.current);
    };
  }, []);

  if (isLoading) {
    return <Loading />; // Muestra el spinner mientras se cargan los datos
  }
  return (
    <Edit
      // I don't need any redirection here, there's no list page
      {...props}
      id={0}
      resource="availabilityWeb"
      basePath="/availabilityWeb"
      redirect={false}
      title="Disponibilidad"
    >
      <TabbedForm onSubmit={handleSave}>
        <FormTab label="Disponibilidad">
          <div
            data-tour="horario-atencion"
            style={highlight ? { boxShadow: "0 0 0 4px #1976d2", borderRadius: 8, transition: "box-shadow 0.3s" } : {}}
          >
            <ArrayInput validate={[required()]} source="availability" label="Disponibilidad">
              <SimpleFormIterator addButtonProps={{ "data-tour": "add-horario", onClick: handleAddHorarioClick }}>
                <FormDataConsumer>
                  {({
                    formData, // The whole form data
                    scopedFormData, // The data for this item of the ArrayInput
                    getSource, // A function to get the valid source inside an ArrayInput
                    ..._rest
                  }) => (
                    <div data-tour="dias-semana">
                      <SelectInput
                        validate={[required()]}
                        source={getSource("day")}
                        label="Día"
                        choices={[
                          { id: "L", name: "Lunes" },
                          { id: "M", name: "Martes" },
                          { id: "MM", name: "Miércoles" },
                          { id: "J", name: "Jueves" },
                          { id: "V", name: "Viernes" },
                          { id: "S", name: "Sábado" },
                          { id: "D", name: "Domingo" },
                        ]}
                      />
                    </div>
                  )}
                </FormDataConsumer>

                <FormDataConsumer formClassName={classes.inlineBlock}>
                  {({
                    formData, // The whole form data
                    scopedFormData, // The data for this item of the ArrayInput
                    getSource, // A function to get the valid source inside an ArrayInput
                    ..._rest
                  }) => (
                    <SelectInput
                      validate={[required()]}
                      source={getSource("startHour")}
                      label="Hora de Inicio"
                      choices={getInitialFor(scopedFormData?.endHour)}
                    />
                  )}
                </FormDataConsumer>

                <FormDataConsumer formClassName={classes.inlineBlock}>
                  {({
                    formData, // The whole form data
                    scopedFormData, // The data for this item of the ArrayInput
                    getSource, // A function to get the valid source inside an ArrayInput
                    ..._rest
                  }) => (
                    <SelectInput
                      validate={[required()]}
                      source={getSource("startMinute")}
                      label="Minuto de inicio"
                      choices={[
                        { id: "0", name: "00" },
                        { id: "30", name: "30" },
                      ]}
                    />
                  )}
                </FormDataConsumer>

                <FormDataConsumer formClassName={classes.inlineBlock}>
                  {({
                    formData, // The whole form data
                    scopedFormData, // The data for this item of the ArrayInput
                    getSource, // A function to get the valid source inside an ArrayInput
                    ..._rest
                  }) => (
                    <SelectInput
                      validate={[required()]}
                      source={getSource("endHour")}
                      label="Hora Final"
                      choices={getFinalFor(scopedFormData?.startHour)}
                      formClassName={classes.inlineBlock}
                    />
                  )}
                </FormDataConsumer>

                <FormDataConsumer formClassName={classes.inlineBlock}>
                  {({
                    formData, // The whole form data
                    scopedFormData, // The data for this item of the ArrayInput
                    getSource, // A function to get the valid source inside an ArrayInput
                    ..._rest
                  }) => (
                    <SelectInput
                      validate={[required()]}
                      source={getSource("endMinute")}
                      label="Minuto final"
                      choices={[
                        { id: "0", name: "00" },
                        { id: "30", name: "30" },
                      ]}
                    />
                  )}
                </FormDataConsumer>
              </SimpleFormIterator>
            </ArrayInput>
          </div>
        </FormTab>
      </TabbedForm>
    </Edit>
  );
};
